import axios from 'axios'

import PayloadPostSimulacao from '@/models/api/PayloadPostSimulacao'
import Utm from '@/models/Utm'
import { HttpStatus } from '@/services/BaseService/types'
import SimulacaoService from '@/services/SimulacaoService'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import routes from '@/routes/types'
import ResponseSimulacao from '@/models/api/ResponseSimulacao'
import ResponseSimulacaoAutorizadaPost from '@/models/api/ResponseSimulacaoAutorizada'
import PayloadGetSimulacao from '@/models/api/PayloadGetSimulacao'
import type { ErrorRouteTelemetry } from '@/models/api/Payload'
import validarErro, {
  buildErrorRouteTelemetry,
  getHttpStatusFromUnknownError,
} from '@/utils/validarErro'
import { capSaldoFgtsForApi } from '@/utils'
import PayloadPutDadosOriginacao from '@/models/api/PayloadPutDadosOriginacao'
import BaseService from '@/services/BaseService'
import { WbEnvironments } from '@interco/inter-webview-bridge'
import PropostaService from '@/services/PropostaService'

import { RootState } from '..'

export interface ErroSimulacaoWeb {
  erro: Erro
  status: number
}

export interface ErroSimulacaoMobile {
  action: string
  message: string
  httpStatus: number
  response: string
}

export interface Response {
  erro: Erro
  status: number
}

export interface Erro {
  mensagem: string
  mensagemDetalhe: string
}

export interface SimulacaoState {
  maxCashback: number
  loading: boolean
  error: boolean
  utm: Utm
  data?: ResponseSimulacao
  naoAutorizadoState: NaoAutorizadoState
  autorizadoState: AutorizadoState
  isCashBack: boolean
  fluxoAlternativo: boolean
  callBack?: string
  isSimulating: boolean
  lastSimulationTime: number
}

export interface NaoAutorizadoState {
  saldo: number
  qtdAnos: number
  maxParcela: number
}

export interface AutorizadoState {
  qtdAnos: number
  data?: ResponseSimulacaoAutorizadaPost
  qtdMaxNumParcela: number
  isRaspaSaldo: boolean
  uuidRaspaSaldo?: string
}

const initialState: SimulacaoState = {
  maxCashback: 200.0, // TODO: buscar valor do cashback
  loading: false,
  error: false,
  utm: {
    campaing: '',
    content: '',
    medium: '',
    source: '',
  },
  naoAutorizadoState: {
    qtdAnos: 5,
    maxParcela: 5,
    saldo: 0,
  },
  autorizadoState: {
    qtdAnos: 5,
    qtdMaxNumParcela: 5,
    isRaspaSaldo: false,
  },
  isCashBack: false,
  fluxoAlternativo: false,
  isSimulating: false,
  lastSimulationTime: 0,
}

// Temporary flag to block simulations when Caixa is unavailable.
// Production: keep unset or REACT_APP_CAIXA_INDISPONIVEL=true (default blocks).
// Tests: set REACT_APP_CAIXA_INDISPONIVEL=false before loading this module.
// TODO: Remove this flag after Caixa maintenance period (3 days)
const isCaixaIndisponivel = (): boolean => {
  const envFlag = process.env.REACT_APP_CAIXA_INDISPONIVEL
  if (envFlag === 'false') return false
  if (envFlag === 'true') return true
  return false
}

/**
 * Returned when axios/mobile payload cannot be parsed or has no `erro.mensagem`.
 * Maps to ERRO_RESPOSTA_INVALIDA in validarErro — distinct from real SERVICO_TEMPORARIAMENTE_INDISPONIVEL from API.
 */
export const SIMULACAO_ERROR_EXTRACTION_FAILED_MARKER = '__ERROR_EXTRACTION_FAILED__' as const

export const getMensagemErro = (e: unknown): string => {
  try {
    if (BaseService.getEnvironment() === WbEnvironments.BROWSER && axios.isAxiosError(e)) {
      const data = e?.response?.data as ErroSimulacaoWeb | undefined
      const webApiMessage = data?.erro?.mensagem
      if (typeof webApiMessage === 'string' && webApiMessage.trim() !== '') {
        return webApiMessage
      }
      return SIMULACAO_ERROR_EXTRACTION_FAILED_MARKER
    }

    const erro = (JSON.parse(e as string) as ErroSimulacaoMobile).response
    const inner = JSON.parse(erro as string) as Response
    const bridgeApiMessage = inner?.erro?.mensagem
    if (typeof bridgeApiMessage === 'string' && bridgeApiMessage.trim() !== '') {
      return bridgeApiMessage
    }
    return SIMULACAO_ERROR_EXTRACTION_FAILED_MARKER
  } catch {
    return SIMULACAO_ERROR_EXTRACTION_FAILED_MARKER
  }
}

const callBackSimulacao = (
  sucess: (rotas: string, code?: string, status?: number, parcela?: number) => void,
  status: number,
  isAutorizado: boolean,
  nuParcela?: number,
  tipoSimulacao = 'SINCRONA',
) => {
  if (isAutorizado && status === HttpStatus.CREATED && tipoSimulacao === 'ASSINCRONA') {
    sucess(routes.ERROR, 'SIMULACAO_ASSINCRONA')
  } else if (
    (isAutorizado && status === HttpStatus.CREATED && tipoSimulacao === 'SINCRONA') ||
    status === HttpStatus.NO_CONTENT
  ) {
    sucess(routes.ESPERA, undefined, status)
  } else {
    sucess(
      isAutorizado ? routes.AUTORIZADO_RESULTADO : routes.NAO_AUTORIZADO_RESULTADO,
      undefined,
      status,
      nuParcela,
    )
  }
}

type PostSimulacaoErrorCallback = NonNullable<PayloadPostSimulacao['error']>

const handlePostSimulacaoThunkCatchErrors = async (
  errorCallback: PostSimulacaoErrorCallback,
  e: unknown,
): Promise<void> => {
  const messageError = getMensagemErro(e)

  switch (messageError) {
    case 'PENDENTE_CONSULTA_SALDO':
      errorCallback(routes.ESPERA)
      return
    case 'PROCESSO_ANDAMENTO': {
      const user = await BaseService.getUserInfo()
      const { data } = await PropostaService.getAcompanharProposta(user?.cpf, user?.account)
      errorCallback(`${routes.PROPOSTA}?id=${data.uuidSimulacao}`)
      return
    }
    case 'NAO_AUTORIZADO_RECORRENCIA':
      errorCallback(routes.ERROR, 'NAO_AUTORIZADO')
      return
    case 'SEM_ADESAO_SAQUE_ANIVERSARIO_RECORRENCIA':
      errorCallback(routes.ERROR, 'SEM_ADESAO_SAQUE_ANIVERSARIO')
      return
    default: {
      const rawApiMessage = typeof messageError === 'string' ? messageError : undefined
      const errorCode = validarErro(rawApiMessage)
      errorCallback(
        routes.ERROR,
        errorCode,
        buildErrorRouteTelemetry(rawApiMessage, errorCode, {
          httpStatus: getHttpStatusFromUnknownError(e),
          ...(messageError === SIMULACAO_ERROR_EXTRACTION_FAILED_MARKER
            ? { isResponseExtractionFailed: true }
            : {}),
        }),
      )

      BaseService.sendAnalyticsEvent({
        name: 'ANALISE_ERRO_SIMULACAO_FGTS',
        params: {
          error_code: errorCode,
          raw_api_message: rawApiMessage ?? '',
          http_status: String(getHttpStatusFromUnknownError(e) ?? 'http_status fail'),
          message_error: messageError,
          error_message: e instanceof Error ? e.message : 'instanceof erro fail',
        },
      })
    }
  }
}

export const postSimulacaoThunk = createAsyncThunk(
  'simulacao/postSimulacao',
  async (payload: PayloadPostSimulacao, { rejectWithValue }) => {
    // Block simulation if Caixa is unavailable - NO API CALL IS MADE
    if (isCaixaIndisponivel()) {
      // Call error callback immediately to redirect user
      if (payload.error) {
        payload.error(routes.ERROR, 'SERVICO_CEF_INDISPONIVEL')
      }
      // Return rejected to trigger error state in reducer
      // This ensures loading state is set to false
      return rejectWithValue({
        message: 'Caixa temporarily unavailable',
        code: 'SERVICO_CEF_INDISPONIVEL',
      })
    }

    try {
      const user = await BaseService.getUserInfo()

      const payloadForApi: PayloadPostSimulacao = {
        ...payload,
        saldoFgts:
          payload.saldoFgts === undefined || !Number.isFinite(payload.saldoFgts)
            ? undefined
            : capSaldoFgtsForApi(payload.saldoFgts),
      }

      const { data, status } = await SimulacaoService.postSimulacao(payloadForApi, user?.account)

      if (payload.sucess) {
        callBackSimulacao(
          payload.sucess,
          status,
          payload.autorizaConsultaCaixa,
          data?.nuParcelas || 5,
          data?.tipoSimulacao,
        )
      }

      return data
    } catch (e) {
      if (payload.error) {
        await handlePostSimulacaoThunkCatchErrors(payload.error, e)
      }

      return rejectWithValue(e)
    }
  },
)
export const getSimulacaoThunk = createAsyncThunk(
  'simulacao/getSimulacao',
  async (payload: PayloadGetSimulacao, { rejectWithValue }) => {
    try {
      const user = await BaseService.getUserInfo()

      const { data, status } = await SimulacaoService.getSimulacao(
        payload.uuid,
        user?.cpf,
        user?.account,
      )

      if (payload.sucess) {
        callBackSimulacao(
          payload.sucess,
          status,
          payload.autorizaConsultaCaixa,
          data?.nuParcelas || 5,
        )
      }

      return data
    } catch (e) {
      if (payload.error) {
        const messageError = getMensagemErro(e)
        if (messageError === 'PENDENTE_CONSULTA_SALDO') {
          payload.error(routes.ESPERA)
        } else if (
          messageError === 'NAO_AUTORIZADO_RECORRENCIA' &&
          payload?.fromPage &&
          payload?.fromPage === routes.HOME
        ) {
          payload?.error(routes.ERROR, 'NAO_AUTORIZADO')
        } else {
          const rawApiMessage = typeof messageError === 'string' ? messageError : undefined
          const resolved = validarErro(rawApiMessage)
          payload?.error(
            routes.ERROR,
            resolved,
            buildErrorRouteTelemetry(rawApiMessage, resolved, {
              httpStatus: getHttpStatusFromUnknownError(e),
              ...(messageError === SIMULACAO_ERROR_EXTRACTION_FAILED_MARKER
                ? { isResponseExtractionFailed: true }
                : {}),
            }),
          )
        }
      }

      return rejectWithValue(e)
    }
  },
)

export const putSimulacaoDadosOriginacaoThunk = createAsyncThunk(
  'simulacao/putSimulacaoDadosOriginacaoo',
  async (payload: PayloadPutDadosOriginacao, { rejectWithValue }) => {
    // Block simulation update if Caixa is unavailable - NO API CALL IS MADE
    // Comentado para NÃO impedir clientes que já efetuaram a consulta de saldo de continuar a contratação.
    // if (isCaixaIndisponivel()) {
    //   // Call error callback immediately to redirect user
    //   if (payload.error) {
    //     payload.error(routes.ERROR, 'SERVICO_INDISPONIVEL_CAIXA')
    //   }
    //   // Return rejected to trigger error state in reducer
    //   // This ensures loading state is set to false
    //   return rejectWithValue({
    //     message: 'Caixa temporarily unavailable',
    //     code: 'SERVICO_INDISPONIVEL_CAIXA',
    //   })
    // }

    try {
      const user = await BaseService.getUserInfo()

      const saldoFgtsParaApi =
        payload.saldoFgts === undefined || !Number.isFinite(payload.saldoFgts)
          ? undefined
          : capSaldoFgtsForApi(payload.saldoFgts)

      const { data } = await SimulacaoService.putSimulacaoDadosOriginacao(
        payload.uuid,
        payload.qtdPeriodos,
        user?.account,
        payload.autorizaConsultaCaixa,
        saldoFgtsParaApi,
      )

      if (payload.success) {
        if (data.limiteFlexivel) {
          payload.success(routes.LIMITE_FLEXIVEL, data.nuParcelas)
        } else {
          payload.success(routes.AUTORIZADO_RESULTADO, data.nuParcelas)
        }
      }
      return {
        data,
        isAutorizado: payload.autorizaConsultaCaixa,
      }
    } catch (e) {
      if (payload.error) {
        const messageError = getMensagemErro(e)
        const rawApiMessage = typeof messageError === 'string' ? messageError : undefined
        const errorCode = validarErro(rawApiMessage)
        payload.error(
          routes.ERROR,
          errorCode,
          buildErrorRouteTelemetry(rawApiMessage, errorCode, {
            httpStatus: getHttpStatusFromUnknownError(e),
            ...(messageError === SIMULACAO_ERROR_EXTRACTION_FAILED_MARKER
              ? { isResponseExtractionFailed: true }
              : {}),
          }),
        )

        BaseService.sendAnalyticsEvent({
          name: 'ANALISE_ERRO_SIMULACAO_FGTS',
          params: {
            error_code: errorCode,
            raw_api_message: rawApiMessage ?? '',
            http_status: String(getHttpStatusFromUnknownError(e) ?? 'http_status fail'),
            message_error: messageError,
            error_message: e instanceof Error ? e.message : 'instanceof erro fail',
          },
        })
      }

      return rejectWithValue(e)
    }
  },
)

export const getCashBack = createAsyncThunk(
  'simulacao/cashback',
  async (payload: { produto: string }, { rejectWithValue }) => {
    try {
      const { data } = await SimulacaoService.getCashBack(payload.produto)

      return data
    } catch (e) {
      return rejectWithValue(e)
    }
  },
)

export const getAutorizacoes = createAsyncThunk(
  'simulacao/autorizacoesCEF',
  async (
    payload: {
      callback: (isExisteCPF: boolean, autorizacao: boolean | undefined) => void
      erro: () => void
    },
    { rejectWithValue },
  ) => {
    try {
      const user = await BaseService.getUserInfo()

      const cpf = user?.cpf || process.env.REACT_APP_AUTORIZACOES_CPF || ''

      const response = await SimulacaoService.getAutorizacoesCEF(cpf)

      const data = response?.data?.autorizacoes

      payload.callback(
        !!data.length,
        data.some((autorizacao) => autorizacao.autorizado),
      )

      return data
    } catch (e) {
      payload.erro()

      return rejectWithValue(e)
    }
  },
)

export const postNovoSaldo = createAsyncThunk(
  'simulacao/novoSaldo',
  async (
    payload: {
      callback: (isSaldoAtualizado: boolean, uuid: string) => void
      erro: (msg: string, errorCode?: string, telemetry?: ErrorRouteTelemetry) => void
      idAntecipacaoFgts: string
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState
      const user = await BaseService.getUserInfo()

      const response = await SimulacaoService.postNovoSaldo(
        payload.idAntecipacaoFgts,
        state.simulacao.data?.valorTotalOperacao as number,
        state.simulacao.data?.valorLiberado as number,
        user?.account,
      )
      payload.callback(response.data.saldoAtualizado, response.data?.idNovaSimulacao || '')

      return response.data
    } catch (e) {
      const messageError = getMensagemErro(e)
      const rawApiMessage = typeof messageError === 'string' ? messageError : undefined
      const errorCode = validarErro(rawApiMessage)
      payload.erro(
        String(e),
        errorCode,
        buildErrorRouteTelemetry(rawApiMessage, errorCode, {
          httpStatus: getHttpStatusFromUnknownError(e),
          ...(messageError === SIMULACAO_ERROR_EXTRACTION_FAILED_MARKER
            ? { isResponseExtractionFailed: true }
            : {}),
        }),
      )
      return rejectWithValue(e)
    }
  },
)

export const postNovoSaldoValidacao = createAsyncThunk(
  'simulacao/novoSaldoValidacao',
  async (
    payload: {
      callback: (isSaldoAtualizado: boolean) => void
      erro: (erro: string) => void
    },
    { rejectWithValue },
  ) => {
    try {
      const user = await BaseService.getUserInfo()

      const response = await SimulacaoService.postNovoSaldoValidacao(user?.account)
      payload.callback(response.data.isAptoRaspaSaldo)

      return response.data
    } catch (e) {
      payload.erro(String(e))

      return rejectWithValue(e)
    }
  },
)

const simulacaoSlice = createSlice({
  name: 'simulacao',
  initialState,
  reducers: {
    setMaxCashback: (state, action: PayloadAction<number>) => {
      state.maxCashback = action.payload
    },
    setUtm: (state, action: PayloadAction<Utm>) => {
      state.utm = action.payload
    },
    setSaldo: (state, action: PayloadAction<number>) => {
      state.naoAutorizadoState.saldo = action.payload
    },
    setQtdAnosNaoAutorizado: (state, action: PayloadAction<number>) => {
      state.naoAutorizadoState.qtdAnos = action.payload
    },
    setQtdAnosAutorizado: (state, action: PayloadAction<number>) => {
      state.autorizadoState.qtdAnos = action.payload
    },
    setMaxParcelaAutorizado: (state, action: PayloadAction<number>) => {
      state.autorizadoState.qtdMaxNumParcela = action.payload
    },
    setMaxParcelaNaoAutorizado: (state, action: PayloadAction<number>) => {
      state.naoAutorizadoState.maxParcela = action.payload
    },
    resetAutorizado: (state) => {
      state.autorizadoState = initialState.autorizadoState
    },
    setFluxoAlternativo: (state, action: PayloadAction<boolean>) => {
      state.fluxoAlternativo = action.payload
    },
    setCallback: (state, action: PayloadAction<string | undefined>) => {
      state.callBack = action.payload
    },
    setRaspaSaldo: (state, action: PayloadAction<boolean>) => {
      state.autorizadoState.isRaspaSaldo = action.payload
    },

    clearUuidRaspaSaldo: (state) => {
      state.autorizadoState.uuidRaspaSaldo = undefined
    },
    setSimulating: (state, action: PayloadAction<boolean>) => {
      state.isSimulating = action.payload
      if (action.payload) {
        state.lastSimulationTime = Date.now()
      }
    },
    resetSimulating: (state) => {
      state.isSimulating = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(postSimulacaoThunk.fulfilled, (state, action) => {
        state.error = false
        state.loading = false
        // Don't reset isSimulating here - let the component handle it after navigation
        if (isSimulacaoAutorizada(action.payload)) {
          state.autorizadoState.data = action.payload as ResponseSimulacaoAutorizadaPost
        } else {
          state.data = action.payload as ResponseSimulacao
        }
      })
      .addCase(postSimulacaoThunk.pending, (state) => {
        state.loading = true
        state.isSimulating = true
      })
      .addCase(postSimulacaoThunk.rejected, (state) => {
        state.loading = false
        state.error = true
        state.isSimulating = false
      })
    builder
      .addCase(getSimulacaoThunk.fulfilled, (state, action) => {
        state.error = false
        state.loading = false
        state.data = action.payload as ResponseSimulacao
      })
      .addCase(getSimulacaoThunk.pending, (state) => {
        state.loading = true
      })
      .addCase(getSimulacaoThunk.rejected, (state) => {
        state.loading = false
        state.error = true
      })
    builder
      .addCase(putSimulacaoDadosOriginacaoThunk.fulfilled, (state, action) => {
        state.error = false
        state.loading = false
        state.data = action.payload.data as ResponseSimulacao
        if (action.payload.isAutorizado) {
          const data: ResponseSimulacaoAutorizadaPost = {
            dataOperacao: action.payload.data.dataOperacao,
            uuidSimulacao: action.payload.data.uuidSimulacao,
            nuParcelas: action.payload.data.nuParcelas,
            uuidProposta: action.payload.data.uuidProposta || '',
          }
          state.autorizadoState.data = data
        }
      })
      .addCase(putSimulacaoDadosOriginacaoThunk.pending, (state) => {
        state.loading = true
      })
      .addCase(putSimulacaoDadosOriginacaoThunk.rejected, (state) => {
        state.loading = false
        state.error = true
      })

    builder
      .addCase(getCashBack.fulfilled, (state, action) => {
        state.error = false
        state.loading = false
        state.isCashBack = action.payload.cashbackDisponivelParaSimular
      })
      .addCase(getCashBack.pending, (state) => {
        state.loading = true
      })
      .addCase(getCashBack.rejected, (state) => {
        state.loading = false
        state.error = true
        state.isCashBack = false
      })

    builder
      .addCase(getAutorizacoes.fulfilled, (state) => {
        state.error = false
        state.loading = false
        // Don't reset isSimulating here - let postSimulacaoThunk handle it
      })
      .addCase(getAutorizacoes.pending, (state) => {
        state.loading = true
        state.isSimulating = true
      })
      .addCase(getAutorizacoes.rejected, (state) => {
        state.loading = false
        state.error = true
        state.isSimulating = false
      })

    builder
      .addCase(postNovoSaldo.fulfilled, (state, action) => {
        state.error = false
        state.loading = false
        if (state?.data?.uuidSimulacao && action.payload?.idNovaSimulacao) {
          state.autorizadoState.uuidRaspaSaldo = action.payload?.idNovaSimulacao || ''
          state.autorizadoState.isRaspaSaldo = true
        }
      })
      .addCase(postNovoSaldo.pending, (state) => {
        state.loading = true
        state.autorizadoState.isRaspaSaldo = false
      })
      .addCase(postNovoSaldo.rejected, (state) => {
        state.loading = false
        state.error = true
        state.autorizadoState.isRaspaSaldo = false
      })

    builder
      .addCase(postNovoSaldoValidacao.fulfilled, (state) => {
        state.error = false
        state.loading = false
      })
      .addCase(postNovoSaldoValidacao.pending, (state) => {
        state.loading = true
      })
      .addCase(postNovoSaldoValidacao.rejected, (state) => {
        state.loading = false
        state.error = true
      })
  },
})

function isSimulacaoAutorizada(
  object: ResponseSimulacao | ResponseSimulacaoAutorizadaPost,
): object is ResponseSimulacaoAutorizadaPost {
  if (object === null) {
    return false
  }
  return 'uuidProposta' in object
}

export const [simulacaoReducer, simulacaoActions] = [simulacaoSlice.reducer, simulacaoSlice.actions]
